import { useEffect, useMemo, useRef, type ReactNode } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useFormatDate } from "@/hooks/use-format-date";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Clock, Loader2, RefreshCw, ArrowUpRight, ShieldAlert, XCircle } from "lucide-react";
import {
  analyzeAppPayloadSchema,
  analyzeQvdsPayloadSchema,
  auditGroupPayloadSchema,
  syncAppPayloadSchema,
  syncGroupPayloadSchema,
  type Job,
} from "@repo/shared";
import {
  Alert,
  Button,
  Card,
  CardContent,
  ErrorPage,
  FeedbackState,
  MetricCard,
  MetricStrip,
  PageContent,
  PageHeader,
  PageShell,
  Progress,
  PropertyList,
  StatusBadge,
  type PropertyItem,
  type StatusBadgeProps,
} from "@h2j-data-team/ui";

import { BackLink } from "@/components/back-link";
import { supportCode } from "@/components/error-screen";
import { toast } from "@/components/shared";
import { useReconcileSingleAppDraft } from "@/features/single-app-drafts/api";
import { makeJobTypeLabeler } from "@/features/jobs/labels";
import { jobKeys, jobQueryOptions, useRetryJob, useCancelJob } from "@/features/jobs/api";
import { ResultsTables } from "@/features/jobs/components/results-tables";
import { appAnalysisKeys, appAnalysisListQueryOptions } from "@/features/app-analysis/api";
import {
  qvdAnalysisRunQueryOptions,
  useCancelQvdAnalysis,
  isRunActive,
} from "@/features/qvd-analysis/api";
import { QvdKpiCells } from "@/components/qvd-analysis";
import { discardAppQueries } from "@/features/qlik-connections/api";
import { ApiError, getErrorMessage } from "@/lib/api-client";
import { formatMeasurementProgress } from "@/lib/qlik-evaluation-format";

export const Route = createFileRoute("/_authenticated/jobs/$id")({
  staticData: { pageTitle: "breadcrumbs.jobDetails", breadcrumb: "breadcrumbs.jobDetails" },
  component: JobDetailsPage,
});

type StatusTone = NonNullable<StatusBadgeProps["tone"]>;

const STATUS_TONE: Record<Job["status"], StatusTone> = {
  pending: "neutral",
  processing: "info",
  completed: "success",
  failed: "danger",
};

function getJobSourceLink(
  job: Job,
  t: (key: string, opts?: Record<string, unknown>) => string,
):
  | {
      to: "/single-app/$connectionId/apps/$appId";
      params: { connectionId: string; appId: string };
      label: string;
    }
  | {
      to: "/sync-groups/$groupId";
      params: { groupId: string };
      label: string;
    }
  | null {
  if (job.type === "sync_app") {
    const parsedPayload = syncAppPayloadSchema.safeParse(job.payload);
    if (!parsedPayload.success) {
      return null;
    }

    const sourceChange =
      parsedPayload.data.variableChanges?.[0] ?? parsedPayload.data.masterItemChanges?.[0];

    if (!sourceChange) {
      return null;
    }

    return {
      to: "/single-app/$connectionId/apps/$appId",
      params: {
        connectionId: sourceChange.connectionId,
        appId: sourceChange.appId,
      },
      label: sourceChange.appName
        ? t("detail.openApp", { name: sourceChange.appName })
        : t("detail.openSourceApp"),
    };
  }

  if (job.type === "sync_group") {
    const parsedPayload = syncGroupPayloadSchema.safeParse(job.payload);
    if (!parsedPayload.success) {
      return null;
    }

    return {
      to: "/sync-groups/$groupId",
      params: { groupId: parsedPayload.data.groupId },
      label: t("detail.openSyncGroup"),
    };
  }

  if (job.type === "audit_group") {
    const parsedPayload = auditGroupPayloadSchema.safeParse(job.payload);
    if (!parsedPayload.success) {
      return null;
    }

    return {
      to: "/sync-groups/$groupId",
      params: { groupId: parsedPayload.data.groupId },
      label: t("detail.openSyncGroup"),
    };
  }

  if (job.type === "analyze_app") {
    const parsedPayload = analyzeAppPayloadSchema.safeParse(job.payload);
    if (!parsedPayload.success) {
      return null;
    }

    return {
      to: "/single-app/$connectionId/apps/$appId",
      params: {
        connectionId: parsedPayload.data.connectionId,
        appId: parsedPayload.data.appId,
      },
      label: parsedPayload.data.appName
        ? t("detail.openApp", { name: parsedPayload.data.appName })
        : t("detail.openSourceApp"),
    };
  }

  return null;
}

/** For completed analyze_app jobs, resolve the analysis ID to link to. */
function useAnalysisLink(job: Job | undefined) {
  const parsedPayload =
    job?.type === "analyze_app" ? analyzeAppPayloadSchema.safeParse(job.payload) : undefined;

  const shouldFetch =
    job?.type === "analyze_app" && job.status === "completed" && parsedPayload?.success;

  const { data } = useQuery(
    appAnalysisListQueryOptions(
      shouldFetch
        ? {
            connectionId: parsedPayload!.data!.connectionId,
            appId: parsedPayload!.data!.appId,
            limit: 1,
          }
        : undefined,
    ),
  );

  if (!shouldFetch) return null;

  const analysisId = data?.data?.[0]?.id;
  return analysisId ?? null;
}

// Total execution time between a job's start and completion, e.g. "2m 30s".
function formatExecutionTime(startedAt: string, completedAt: string): string {
  const ms = new Date(completedAt).getTime() - new Date(startedAt).getTime();
  if (!Number.isFinite(ms) || ms < 0) return "—";
  if (ms < 1000) return `${ms}ms`;
  const totalSeconds = Math.floor(ms / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const totalMinutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (totalMinutes < 60) return `${totalMinutes}m ${seconds}s`;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours}h ${minutes}m ${seconds}s`;
}

function JobDetailsPage() {
  const { t: tAnalyses } = useTranslation("analyses");
  const { id } = Route.useParams();
  const { t } = useTranslation("jobs");
  const { t: tCommon } = useTranslation("common");
  const { t: tQvd } = useTranslation("qvd-analysis");
  const jobTypeLabel = useMemo(() => makeJobTypeLabeler(t), [t]);
  const { formatDateTime } = useFormatDate();
  const { data: job, isLoading, error, refetch } = useQuery(jobQueryOptions(id));
  const isQvdJob = job?.type === "analyze_qvds";
  const qvdPayload =
    job?.type === "analyze_qvds" ? analyzeQvdsPayloadSchema.safeParse(job.payload) : undefined;
  const qvdRunId = qvdPayload?.success ? qvdPayload.data.runId : "";
  const retryMutation = useRetryJob();
  const cancelMutation = useCancelJob();
  const cancelQvdMutation = useCancelQvdAnalysis();
  // For QVD jobs, live progress + coverage come from the run (the generic
  // job.progress/result are null/misleading). The query self-polls while active.
  const { data: qvdRun } = useQuery(qvdAnalysisRunQueryOptions(qvdRunId));
  const queryClient = useQueryClient();
  const prevStatusRef = useRef<string | undefined>(undefined);
  const syncAppSource =
    job?.type === "sync_app"
      ? ((
          job.payload as {
            variableChanges?: { connectionId: string; appId: string }[];
            masterItemChanges?: { connectionId: string; appId: string }[];
          }
        ).variableChanges?.[0] ??
        (
          job.payload as {
            variableChanges?: { connectionId: string; appId: string }[];
            masterItemChanges?: { connectionId: string; appId: string }[];
          }
        ).masterItemChanges?.[0])
      : undefined;
  const reconcileDraftMutation = useReconcileSingleAppDraft(
    syncAppSource?.connectionId ?? "",
    syncAppSource?.appId ?? "",
  );

  // Invalidate analyses cache when an analyze_app job completes
  useEffect(() => {
    if (!job) return;
    const prevStatus = prevStatusRef.current;
    prevStatusRef.current = job.status;

    if (
      job.type === "analyze_app" &&
      job.status === "completed" &&
      prevStatus &&
      prevStatus !== "completed"
    ) {
      queryClient.invalidateQueries({ queryKey: appAnalysisKeys.lists() });
    }

    if (
      job.type === "sync_app" &&
      job.status === "completed" &&
      prevStatus &&
      prevStatus !== "completed" &&
      syncAppSource
    ) {
      void discardAppQueries(queryClient, syncAppSource.connectionId, syncAppSource.appId);
    }

    if (
      job.type === "sync_app" &&
      prevStatus &&
      (prevStatus === "pending" || prevStatus === "processing") &&
      job.status !== "pending" &&
      job.status !== "processing"
    ) {
      reconcileDraftMutation.mutateAsync(job.id).catch((err) => {
        toast.error(getErrorMessage(err, t("detail.draftReconcileFailed")));
      });
    }
  }, [job, queryClient, reconcileDraftMutation, t]);

  // A failure shows in the alert below (retryMutation.error); it is not rethrown.
  const handleRetry = () => retryMutation.mutate(id);

  const analysisId = useAnalysisLink(job);

  const handleCancel = async () => {
    try {
      // Cancelling a QVD job must stop the actual run — the worker watches the
      // run status, not the job. Reuse the same cancel path as the analysis page
      // (sets run -> cancelling AND cancels the underlying job).
      if (isQvdJob && qvdRunId) {
        await cancelQvdMutation.mutateAsync(qvdRunId);
        void queryClient.invalidateQueries({ queryKey: jobKeys.detail(id) });
      } else {
        await cancelMutation.mutateAsync(id);
      }
    } catch (err) {
      toast.error(getErrorMessage(err, t("detail.cancelFailed")));
    }
  };

  const back = <BackLink to="/jobs" label={t("detail.backToJobs")} />;

  if (isLoading) {
    return (
      <PageShell
        header={
          <PageHeader
            title={t("detail.pageTitle")}
            description={t("detail.loadingDescription")}
            back={back}
          />
        }
      >
        <PageContent>
          <FeedbackState state="loading" title={t("detail.loadingDetails")} />
        </PageContent>
      </PageShell>
    );
  }

  if (error || !job) {
    // The server's message never reaches the screen; a 5xx carries its request id.
    const notFound = !error || (error instanceof ApiError && error.status === 404);
    return (
      <ErrorPage
        kind={notFound ? "not-found" : "load-failed"}
        header={<PageHeader title={t("detail.pageTitle")} back={back} />}
        title={notFound ? t("detail.jobNotFound") : t("detail.loadFailed")}
        supportCode={supportCode(error, tCommon("error.supportCode"))}
        actions={
          notFound ? undefined : (
            <Button variant="outline" onClick={() => refetch()}>
              {t("detail.tryAgain")}
            </Button>
          )
        }
      />
    );
  }

  const progress = job.progress;
  const progressPercent =
    progress && progress.total > 0 ? Math.round((progress.completed / progress.total) * 100) : 0;
  const canRetry = job.status === "failed" || job.status === "pending";
  const canCancel = job.status === "pending" || job.status === "processing";
  const sourceLink = getJobSourceLink(job, t);

  // QVD-specific render data (sourced from the run, not the generic job).
  const qvdCoverage = isQvdJob ? qvdRun?.coverage : undefined;
  const qvdActive = isQvdJob && qvdRun ? isRunActive(qvdRun.status) : false;
  const qvdProgress = qvdRun?.progress;
  const qvdProgressPercent =
    qvdProgress && qvdProgress.total > 0
      ? Math.round((qvdProgress.processed / qvdProgress.total) * 100)
      : 0;
  const cancelPending = isQvdJob ? cancelQvdMutation.isPending : cancelMutation.isPending;
  const cancelDisabled = isQvdJob && qvdRun?.status === "cancelling";

  const isExpressionValidationFailure =
    job.type === "sync_group" &&
    job.status === "failed" &&
    job.result?.items?.some((item) => item.error?.startsWith("EXPRESSION_VALIDATION_FAILED"));

  const hasJobProgress = (!isQvdJob && !!progress) || (isQvdJob && qvdActive && !!qvdProgress);
  const hasJobStats = (isQvdJob && !!qvdRun && !!qvdCoverage) || (!isQvdJob && !!job.result);

  const sourceLinkClassName =
    "inline-flex items-center gap-1 text-sm font-medium text-brand-text hover:underline";

  const detailItems: PropertyItem[] = [];
  if (sourceLink || analysisId || (isQvdJob && qvdRunId)) {
    detailItems.push({
      id: "source",
      label: t("detail.sourceResource"),
      full: true,
      value: (
        <div className="flex flex-col gap-1">
          {sourceLink && (
            <Link to={sourceLink.to} params={sourceLink.params} className={sourceLinkClassName}>
              {sourceLink.label}
              <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={1.8} aria-hidden />
            </Link>
          )}
          {analysisId && (
            <Link
              to="/analyses/$analysisId"
              params={{ analysisId }}
              className={sourceLinkClassName}
            >
              {t("detail.viewAnalysis")}
              <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={1.8} aria-hidden />
            </Link>
          )}
          {isQvdJob && qvdRunId && (
            <Link
              to="/qvd-analyses/$runId"
              params={{ runId: qvdRunId }}
              search={{ tab: "orphans" }}
              className={sourceLinkClassName}
            >
              {t("detail.viewQvdAnalysis")}
              <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={1.8} aria-hidden />
            </Link>
          )}
        </div>
      ),
    });
  }
  detailItems.push({
    id: "created",
    label: t("detail.timestamps.created"),
    mono: true,
    value: formatDateTime(job.createdAt),
  });
  if (job.startedAt) {
    detailItems.push({
      id: "started",
      label: t("detail.timestamps.started"),
      mono: true,
      value: formatDateTime(job.startedAt),
    });
  }
  if (job.completedAt) {
    detailItems.push({
      id: "completed",
      label: t("detail.timestamps.completed"),
      mono: true,
      value: formatDateTime(job.completedAt),
    });
  }
  if (job.startedAt && job.completedAt) {
    detailItems.push({
      id: "duration",
      label: t("detail.timestamps.duration"),
      mono: true,
      value: formatExecutionTime(job.startedAt, job.completedAt),
    });
  }

  return (
    <PageShell
      header={
        <PageHeader
          title={jobTypeLabel(job.type)}
          description={t("detail.jobId", { id: job.id.slice(0, 8) })}
          back={back}
          meta={
            <StatusBadge tone={STATUS_TONE[job.status]}>{t(`status.${job.status}`)}</StatusBadge>
          }
          actions={
            <>
              {canCancel && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCancel}
                  loading={cancelPending}
                  disabled={cancelDisabled}
                >
                  <XCircle strokeWidth={1.8} />
                  {cancelPending ? t("detail.cancelling") : t("detail.cancel")}
                </Button>
              )}
              {canRetry && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRetry}
                  loading={retryMutation.isPending}
                >
                  <RefreshCw strokeWidth={1.8} />
                  {retryMutation.isPending ? t("detail.retrying") : t("detail.retry")}
                </Button>
              )}
            </>
          }
        />
      }
    >
      <PageContent>
        <div className="flex flex-col gap-4">
          {/* Retry error message */}
          {retryMutation.error && (
            <Alert tone="danger">
              {t("detail.retryFailed", { error: getErrorMessage(retryMutation.error) })}
            </Alert>
          )}

          {/* Expression validation failure banner */}
          {isExpressionValidationFailure && (
            <Alert
              tone="danger"
              icon={<ShieldAlert strokeWidth={1.8} />}
              title={t("detail.expressionValidationFailed.title")}
            >
              {t("detail.expressionValidationFailed.description")}
            </Alert>
          )}

          {/* Run card: progress + stats */}
          {(hasJobProgress || hasJobStats) && (
            <Card className="gap-0 py-0">
              {!isQvdJob && progress && (
                <RunProgress
                  label={t("detail.progress")}
                  detail={t("detail.progressDetail", {
                    completed: progress.completed,
                    total: progress.total,
                    percent: progressPercent,
                  })}
                  percent={progressPercent}
                  tone={job.status === "failed" ? "danger" : "primary"}
                >
                  {progress.currentItem && job.status === "processing" && (
                    <p className="mt-2 text-xs text-dim">
                      {t("detail.currentlyProcessing")}{" "}
                      <span className="font-mono">
                        {job.type === "analyze_app"
                          ? formatMeasurementProgress(progress.currentItem, tAnalyses)
                          : progress.currentItem}
                      </span>
                    </p>
                  )}
                </RunProgress>
              )}

              {/* QVD live progress: fed by the run (job.progress is null for QVD) */}
              {isQvdJob && qvdActive && qvdProgress && (
                <RunProgress
                  label={t("detail.progress")}
                  detail={t("detail.progressDetail", {
                    completed: qvdProgress.processed,
                    total: qvdProgress.total,
                    percent: qvdProgressPercent,
                  })}
                  percent={qvdProgressPercent}
                  tone="primary"
                >
                  {qvdRun && (
                    <p className="mt-2 text-xs text-dim">{tQvd(`status.phase.${qvdRun.phase}`)}</p>
                  )}
                </RunProgress>
              )}

              {/* QVD coverage indicators: mirror the analysis page KPIs */}
              {isQvdJob && qvdRun && qvdCoverage && (
                <CardContent className="border-t border-hairline py-[18px] first:border-t-0">
                  <QvdKpiCells run={qvdRun} />
                </CardContent>
              )}

              {/* Summary counts */}
              {!isQvdJob && job.result && (
                <CardContent className="border-t border-hairline py-[18px] first:border-t-0">
                  <MetricStrip aria-label={t("detail.summary")}>
                    <MetricCard label={t("detail.total")} value={job.result.summary.total} />
                    <MetricCard
                      label={t("detail.succeeded")}
                      value={
                        <span className="text-status-success">{job.result.summary.succeeded}</span>
                      }
                    />
                    <MetricCard
                      label={t("detail.failed")}
                      value={
                        <span className="text-status-danger">{job.result.summary.failed}</span>
                      }
                    />
                  </MetricStrip>
                </CardContent>
              )}
            </Card>
          )}

          {/* Details card: source + meta */}
          <Card className="gap-0 py-[18px]">
            <CardContent>
              <PropertyList columns={2} items={detailItems} />
            </CardContent>
          </Card>

          {/* Results */}
          {!isQvdJob && job.result && job.result.items && job.result.items.length > 0 && (
            <ResultsTables items={job.result.items} />
          )}

          {/* Processing message */}
          {job.status === "processing" && (
            <Alert tone="info" icon={<Loader2 className="animate-spin" strokeWidth={1.8} />}>
              {t("detail.processing_message")}
            </Alert>
          )}

          {/* Pending message */}
          {job.status === "pending" && (
            <Alert tone="warning" icon={<Clock strokeWidth={1.8} />}>
              {t("detail.pending_message")}
            </Alert>
          )}
        </div>
      </PageContent>
    </PageShell>
  );
}

/** A progress bar of the run card, with the counts in text beside it (so the bar itself is decorative). */
function RunProgress({
  label,
  detail,
  percent,
  tone,
  children,
}: {
  label: string;
  detail: string;
  percent: number;
  tone: "primary" | "danger";
  children?: ReactNode;
}) {
  return (
    <CardContent className="border-t border-hairline py-[18px] first:border-t-0">
      <div className="flex justify-between text-xs">
        <span className="font-semibold uppercase tracking-[.08em] text-faint">{label}</span>
        <span className="font-mono text-dim">{detail}</span>
      </div>
      <Progress value={percent} tone={tone} aria-hidden className="mt-2.5" />
      {children}
    </CardContent>
  );
}
